import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface EmbeddedPage {
  title: string;
  url: string;
  embeddedAt: string;
}

interface DocumentationSource {
  id: string;
  projectId: string;
  sourceType: 'confluence' | 'notion';
  sourceName: string;
  sourceUrl: string | null;
  totalPagesEmbedded: number;
  createdAt: string;
  updatedAt: string;
  pages?: EmbeddedPage[];
}

interface ConfluencePage {
  id: string;
  title: string;
  spaceKey: string;
  spaceName: string;
  url: string;
  isEmbedded?: boolean;
}

interface ConfluenceInstallation {
  id: string;
  cloudId: string;
  siteName: string;
  siteUrl: string;
  installedAt: string;
}

interface ConfluenceSpace {
  id: string;
  key: string;
  name: string;
  type: string;
  status: string;
}

interface NotionInstallation {
  id: string;
  workspaceId: string;
  workspaceName: string;
  installedAt: string;
}

interface NotionDatabase {
  id: string;
  title: string;
  url?: string;
}

interface NotionPage {
  id: string;
  title: string;
  url?: string;
  parentId?: string;
  isEmbedded?: boolean;
}

interface KnowledgeBaseState {
  sources: DocumentationSource[];
  availablePages: ConfluencePage[];
  availableSpaces: ConfluenceSpace[];
  confluenceInstallations: ConfluenceInstallation[];
  // Notion state
  notionInstallations: NotionInstallation[];
  notionDatabases: NotionDatabase[];
  notionPages: NotionPage[];
  notionDatabasesStatus: 'idle' | 'loading' | 'success' | 'failed';
  notionPagesStatus: 'idle' | 'loading' | 'success' | 'failed';
  currentSource: DocumentationSource | null;
  repositorySources: any;
  status: 'idle' | 'loading' | 'success' | 'failed';
  spacesStatus: 'idle' | 'loading' | 'success' | 'failed';
  pagesStatus: 'idle' | 'loading' | 'success' | 'failed';
  repositorySourcesStatus: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: KnowledgeBaseState = {
  sources: [],
  availablePages: [],
  availableSpaces: [],
  confluenceInstallations: [],
  notionInstallations: [],
  notionDatabases: [],
  notionPages: [],
  notionDatabasesStatus: 'idle',
  notionPagesStatus: 'idle',
  currentSource: null,
  repositorySources: null,
  status: 'idle',
  spacesStatus: 'idle',
  pagesStatus: 'idle',
  repositorySourcesStatus: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getConfluenceInstallations = createAsyncThunk(
  'knowledgeBase/getConfluenceInstallations',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/confluence/installations`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getSources = createAsyncThunk(
  'knowledgeBase/getSources',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/sources`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const browseConfluenceSpaces = createAsyncThunk(
  'knowledgeBase/browseConfluenceSpaces',
  async ({ projectId, installationId }: { projectId: string; installationId: string }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ installationId });
      const url = `/knowledge-base/projects/${projectId}/confluence/spaces?${params.toString()}`;
      const response = await apiRequest(url);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const browseConfluencePages = createAsyncThunk(
  'knowledgeBase/browseConfluencePages',
  async ({ projectId, installationId, spaceId }: { projectId: string; installationId: string; spaceId?: string }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ installationId });
      if (spaceId) {
        // Use numeric space ID (not the space key) for Confluence v2 API
        params.append('spaceId', spaceId);
      }
      const url = `/knowledge-base/projects/${projectId}/confluence/browse?${params.toString()}`;
      const response = await apiRequest(url);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const estimateEmbeddingCost = createAsyncThunk(
  'knowledgeBase/estimateEmbeddingCost',
  async (
    { projectId, pageIds, spaceName }: { projectId: string; pageIds: string[]; spaceName: string },
    { rejectWithValue }
  ) => {
    try {
      // Note: installationId is auto-detected by the backend based on project's organization
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/confluence/estimate-cost`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==============================|| NOTION THUNKS ||============================== //

export const getNotionInstallations = createAsyncThunk(
  'knowledgeBase/getNotionInstallations',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/notion/installations`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const browseNotionDatabases = createAsyncThunk(
  'knowledgeBase/browseNotionDatabases',
  async ({ projectId, installationId }: { projectId: string; installationId: string }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ installationId });
      const url = `/knowledge-base/projects/${projectId}/notion/databases?${params.toString()}`;
      const response = await apiRequest(url);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const browseNotionPages = createAsyncThunk(
  'knowledgeBase/browseNotionPages',
  async ({ projectId, installationId, databaseId }: { projectId: string; installationId: string; databaseId?: string }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ installationId });
      if (databaseId) {
        params.append('databaseId', databaseId);
      }
      const url = `/knowledge-base/projects/${projectId}/notion/browse?${params.toString()}`;
      const response = await apiRequest(url);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const estimateNotionEmbeddingCost = createAsyncThunk(
  'knowledgeBase/estimateNotionEmbeddingCost',
  async (
    { projectId, pageIds, spaceName }: { projectId: string; pageIds: string[]; spaceName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/notion/estimate-cost`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const embedNotionPages = createAsyncThunk(
  'knowledgeBase/embedNotionPages',
  async (
    { projectId, pageIds, spaceName }: { projectId: string; pageIds: string[]; spaceName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/notion/embed`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const embedConfluencePages = createAsyncThunk(
  'knowledgeBase/embedConfluencePages',
  async (
    { projectId, pageIds, spaceName }: { projectId: string; pageIds: string[]; spaceName: string },
    { rejectWithValue }
  ) => {
    try {
      // Note: installationId is auto-detected by the backend based on project's organization
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/confluence/embed`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeSource = createAsyncThunk(
  'knowledgeBase/removeSource',
  async ({ projectId, sourceId }: { projectId: string; sourceId: string }, { rejectWithValue }) => {
    try {
      await apiRequest(`/knowledge-base/projects/${projectId}/sources/${sourceId}`, {
        method: 'DELETE',
      });
      return sourceId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const removePage = createAsyncThunk(
  'knowledgeBase/removePage',
  async (
    { projectId, sourceId, documentId }: { projectId: string; sourceId: string; documentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/knowledge-base/projects/${projectId}/sources/${sourceId}/pages/${documentId}`, {
        method: 'DELETE',
      });
      // Unwrap response if wrapped by API interceptor
      const data = response?.response || response;
      return { sourceId, documentId, ...data };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getOrganizationStats = createAsyncThunk(
  'knowledgeBase/getOrganizationStats',
  async (
    { organizationId, projectId, startDate, endDate }: { organizationId: string; projectId?: string; startDate?: string; endDate?: string },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (projectId) queryParams.append('projectId', projectId);
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiRequest(`/knowledge-base/organizations/${organizationId}/stats${params}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getRepositorySources = createAsyncThunk(
  'knowledgeBase/getRepositorySources',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/sources`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get Confluence installations
  [getConfluenceInstallations.pending.type]: { status: 'loading' as const },
  [getConfluenceInstallations.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const installations = action.payload?.response || action.payload || [];
    state.confluenceInstallations = Array.isArray(installations) ? installations : [];
    state.status = 'success';
    state.error = '';
  },
  [getConfluenceInstallations.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch Confluence installations';
  },

  // Get sources
  [getSources.pending.type]: { status: 'loading' as const },
  [getSources.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const sources = action.payload?.response || action.payload || [];
    state.sources = Array.isArray(sources) ? sources : [];
    state.status = 'success';
    state.error = '';
  },
  [getSources.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch documentation sources';
  },

  // Get Notion installations
  [getNotionInstallations.pending.type]: { status: 'loading' as const },
  [getNotionInstallations.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const installations = action.payload?.response || action.payload || [];
    state.notionInstallations = Array.isArray(installations) ? installations : [];
    state.status = 'success';
    state.error = '';
  },
  [getNotionInstallations.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch Notion installations';
  },

  // Browse Notion databases
  [browseNotionDatabases.pending.type]: { notionDatabasesStatus: 'loading' as const },
  [browseNotionDatabases.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const databases = action.payload?.response || action.payload || [];
    state.notionDatabases = Array.isArray(databases) ? databases : [];
    state.notionDatabasesStatus = 'success';
    state.error = '';
  },
  [browseNotionDatabases.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.notionDatabasesStatus = 'failed';
    state.error = action.payload || 'Failed to browse Notion databases';
  },

  // Browse Notion pages
  [browseNotionPages.pending.type]: { notionPagesStatus: 'loading' as const },
  [browseNotionPages.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const pages = action.payload?.response || action.payload || [];
    state.notionPages = Array.isArray(pages) ? pages : [];
    state.notionPagesStatus = 'success';
    state.error = '';
  },
  [browseNotionPages.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.notionPagesStatus = 'failed';
    state.error = action.payload || 'Failed to browse Notion pages';
  },

  // Embed Notion pages
  [embedNotionPages.pending.type]: { status: 'loading' as const },
  [embedNotionPages.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'success';
    state.error = '';
    state.notionPages = [];
  },
  [embedNotionPages.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to embed Notion pages';
  },

  // Browse Confluence spaces
  [browseConfluenceSpaces.pending.type]: { spacesStatus: 'loading' as const },
  [browseConfluenceSpaces.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const spaces = action.payload?.response || action.payload || [];
    state.availableSpaces = Array.isArray(spaces) ? spaces : [];
    state.spacesStatus = 'success';
    state.error = '';
  },
  [browseConfluenceSpaces.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.spacesStatus = 'failed';
    state.error = action.payload || 'Failed to browse Confluence spaces';
  },

  // Browse Confluence pages
  [browseConfluencePages.pending.type]: { pagesStatus: 'loading' as const },
  [browseConfluencePages.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const pages = action.payload?.response || action.payload || [];
    state.availablePages = Array.isArray(pages) ? pages : [];
    state.pagesStatus = 'success';
    state.error = '';
  },
  [browseConfluencePages.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.pagesStatus = 'failed';
    state.error = action.payload || 'Failed to browse Confluence pages';
  },

  // Embed Confluence pages
  [embedConfluencePages.pending.type]: { status: 'loading' as const },
  [embedConfluencePages.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'success';
    state.error = '';
    // Clear available pages after successful embedding
    state.availablePages = [];
  },
  [embedConfluencePages.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to embed Confluence pages';
  },

  // Remove source
  [removeSource.pending.type]: { status: 'loading' as const },
  [removeSource.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    state.sources = state.sources.filter(s => s.id !== action.payload);
    state.status = 'success';
    state.error = '';
  },
  [removeSource.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to remove documentation source';
  },

  // Remove page
  [removePage.pending.type]: { status: 'loading' as const },
  [removePage.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    const { sourceId, documentId, sourceDeleted } = action.payload;
    if (sourceDeleted) {
      // Remove the entire source if it was deleted
      state.sources = state.sources.filter(s => s.id !== sourceId);
    } else {
      // Remove just the page from the source
      const source = state.sources.find(s => s.id === sourceId);
      if (source && source.pages) {
        source.pages = source.pages.filter((p: any) => p.documentId !== documentId);
        source.totalPagesEmbedded = source.pages.length;
      }
    }
    state.status = 'success';
    state.error = '';
  },
  [removePage.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to remove page';
  },

  // Get repository sources
  [getRepositorySources.pending.type]: { repositorySourcesStatus: 'loading' as const },
  [getRepositorySources.fulfilled.type]: (state: KnowledgeBaseState, action: any) => {
    state.repositorySources = action.payload?.response || action.payload;
    state.repositorySourcesStatus = 'success';
    state.error = '';
  },
  [getRepositorySources.rejected.type]: (state: KnowledgeBaseState, action: any) => {
    state.repositorySourcesStatus = 'failed';
    state.error = action.payload || 'Failed to fetch repository knowledge base sources';
  },
};

// ==============================|| SLICE ||============================== //

const slice = createSlice({
  name: 'knowledgeBase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = '';
    },
    clearAvailablePages: (state) => {
      state.availablePages = [];
    },
    clearAvailableSpaces: (state) => {
      state.availableSpaces = [];
    },
    clearNotionDatabases: (state) => {
      state.notionDatabases = [];
    },
    clearNotionPages: (state) => {
      state.notionPages = [];
    },
    clearKnowledgeBaseState: () => initialState,
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type as any, (state, action) => {
        if (typeof handler === 'function') {
          handler(state as any, action);
        } else {
          Object.assign(state, handler);
        }
      });
    });
  },
});

// ==============================|| ACTIONS & REDUCER ||============================== //

export const { clearError, clearAvailablePages, clearAvailableSpaces, clearNotionDatabases, clearNotionPages, clearKnowledgeBaseState } = slice.actions;

export default slice.reducer;

// ==============================|| SELECTORS ||============================== //

export const selectConfluenceInstallations = (state: any) => state.knowledgeBase.confluenceInstallations;
export const selectKnowledgeBaseSources = (state: any) => state.knowledgeBase.sources;
export const selectAvailablePages = (state: any) => state.knowledgeBase.availablePages;
export const selectAvailableSpaces = (state: any) => state.knowledgeBase.availableSpaces;
export const selectCurrentSource = (state: any) => state.knowledgeBase.currentSource;
export const selectRepositorySources = (state: any) => state.knowledgeBase.repositorySources;
export const selectKnowledgeBaseStatus = (state: any) => state.knowledgeBase.status;
export const selectSpacesStatus = (state: any) => state.knowledgeBase.spacesStatus;
export const selectPagesStatus = (state: any) => state.knowledgeBase.pagesStatus;
export const selectRepositorySourcesStatus = (state: any) => state.knowledgeBase.repositorySourcesStatus;
export const selectNotionInstallations = (state: any) => state.knowledgeBase.notionInstallations;
export const selectNotionDatabases = (state: any) => state.knowledgeBase.notionDatabases;
export const selectNotionPages = (state: any) => state.knowledgeBase.notionPages;
export const selectNotionDatabasesStatus = (state: any) => state.knowledgeBase.notionDatabasesStatus;
export const selectNotionPagesStatus = (state: any) => state.knowledgeBase.notionPagesStatus;
export const selectKnowledgeBaseError = (state: any) => state.knowledgeBase.error;
